import { Link as RouterLink } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/esm/ExpandMore';
import RefreshIcon from '@mui/icons-material/esm/Refresh';
import PersonnelFolderCard from '../../components/field/PersonnelFolderCard';
import useAdminFieldData from './useAdminFieldData';
import { FieldPersonnel, seededFieldLoginRows } from '../../routes/fieldPersonnel';
import auditLogService, { AuditTrailItem } from '../../services/api/auditLogService';

type AdminFieldView = 'home' | 'users' | 'teams' | 'field-mode' | 'history';

interface AdminFieldModePageProps {
  view?: AdminFieldView;
}

const fieldFolderRoleLabels = [
  'Project manager',
  'Supervisor',
  'Construction worker login',
  'Stores personnel',
  'Accounts personnel',
  'Executive',
];

const fieldFolderRoleTitles: Record<string, string> = {
  'Project manager': 'Project Manager',
  Supervisor: 'Supervisor',
  'Construction worker login': 'Worker',
  'Stores personnel': 'Stores',
  'Accounts personnel': 'Accounts',
  Executive: 'Executive',
};

const formatDateTime = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const humanize = (value?: string | null) => value ? value.replace(/_/g, ' ') : '-';

export default function AdminFieldModePage({ view = 'home' }: AdminFieldModePageProps) {
  const { users, teams, metrics, loading, error, reload } = useAdminFieldData();
  const [auditRows, setAuditRows] = useState<AuditTrailItem[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState<string | null>(null);
  const [auditSearch, setAuditSearch] = useState('');

  const loadAudit = async () => {
    setAuditLoading(true);
    setAuditError(null);
    try {
      const rows = await auditLogService.list({ search: auditSearch.trim() || undefined, limit: 200 });
      setAuditRows(rows);
    } catch (err: any) {
      setAuditError(err?.response?.data?.message || err?.response?.data?.error || 'Audit history could not be loaded.');
    } finally {
      setAuditLoading(false);
    }
  };

  useEffect(() => {
    if (view === 'history') void loadAudit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const statusAlerts = (
    <>
      {loading && <LinearProgress />}
      {error && (
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={reload}>Retry</Button>}>
          {error}
        </Alert>
      )}
    </>
  );

  const metric = (label: string, value: string, note?: string) => (
    <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1, minHeight: 96 }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>{label}</Typography>
      <Typography variant="h6" fontWeight={900} sx={{ mt: 0.5, lineHeight: 1.15 }}>{value}</Typography>
      {note && <Typography variant="caption" color="text.secondary">{note}</Typography>}
    </Paper>
  );

  const menuBar = (label: string, detail: string, to: string) => (
    <Button
      component={RouterLink}
      to={to}
      fullWidth
      variant="outlined"
      sx={{ justifyContent: 'space-between', minHeight: 70, px: 2, borderRadius: 1, textAlign: 'left', color: 'text.primary', borderColor: 'divider' }}
    >
      <Box>
        <Typography fontWeight={900}>{label}</Typography>
        <Typography variant="caption" color="text.secondary">{detail}</Typography>
      </Box>
      <Typography fontWeight={900}>Open</Typography>
    </Button>
  );


  if (view === 'users') {
    return (
      <Stack spacing={1.5}>
        <Typography variant="h4" fontWeight={900}>Admin</Typography>
        <Typography variant="h6" fontWeight={900}>Users</Typography>
        {statusAlerts}
        {!loading && users.length === 0 && !error && <Alert severity="info">No app users found.</Alert>}
        {users.length > 0 && (
          <Paper sx={{ p: 1.5, borderRadius: 1 }}>
            <Table size="small" sx={{ '& td, & th': { py: 0.55, px: 0.75, fontSize: 12 } }}>
              <TableHead>
                <TableRow><TableCell>User</TableCell><TableCell>Login</TableCell><TableCell>Phone</TableCell><TableCell>Telegram</TableCell></TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <Typography fontWeight={800}>{user.firstName} {user.lastName}</Typography>
                      <Typography variant="caption">{user.role}</Typography>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.phone || '—'}</TableCell>
                    <TableCell>
                      <Chip size="small" label={user.telegramChatId ? 'Linked' : 'Not linked'} color={user.telegramChatId ? 'success' : 'default'} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}
      </Stack>
    );
  }

  if (view === 'teams') {
    const sortedTeams = [...teams].sort((a, b) =>
      a.name.localeCompare(b.name) || a.teamType.localeCompare(b.teamType)
    );
    return (
      <Stack spacing={1.5}>
        <Typography variant="h4" fontWeight={900}>Admin</Typography>
        <Typography variant="h6" fontWeight={900}>Teams</Typography>
        {statusAlerts}
        {!loading && sortedTeams.length === 0 && !error && <Alert severity="info">No teams found.</Alert>}
        <Stack spacing={1}>
          {sortedTeams.map((team) => {
            const members = [...(team.members ?? [])].sort((a, b) =>
              `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)
            );
            const lead = members.find((member) => member.id === team.teamLeadId);
            return (
              <Accordion key={team.id} disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ width: '100%', pr: 1 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography fontWeight={900} noWrap>{team.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Lead: {lead ? `${lead.firstName} ${lead.lastName}` : 'Unassigned'}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" justifyContent="flex-end">
                      <Chip size="small" variant="outlined" label={team.teamType} />
                      <Chip size="small" label={`${members.length} member${members.length === 1 ? '' : 's'}`} />
                    </Stack>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Stack spacing={1}>
                    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography fontWeight={900} sx={{ lineHeight: 1.15 }}>{team.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Lead: {lead ? `${lead.firstName} ${lead.lastName}` : 'Unassigned'}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" justifyContent="flex-end">
                        <Chip size="small" variant="outlined" label={team.teamType} />
                        <Chip size="small" label={`${members.length} member${members.length === 1 ? '' : 's'}`} />
                      </Stack>
                    </Stack>
                    {team.maxConcurrentTasks != null && (
                      <Typography variant="caption" color="text.secondary">
                        Max concurrent tasks: {team.maxConcurrentTasks}
                      </Typography>
                    )}
                    {members.length === 0 ? (
                      <Alert severity="info" sx={{ py: 0 }}>No members assigned.</Alert>
                    ) : (
                      <Stack spacing={0.75}>
                        {members.map((member) => (
                          <Paper key={member.id} variant="outlined" sx={{ p: 1, borderRadius: 1, bgcolor: 'background.default' }}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" fontWeight={800} noWrap>
                                  {member.firstName} {member.lastName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {member.email}
                                </Typography>
                              </Box>
                              <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" justifyContent="flex-end">
                                {member.id === team.teamLeadId && <Chip size="small" color="primary" label="Lead" />}
                                <Chip size="small" variant="outlined" label={member.role} />
                              </Stack>
                            </Stack>
                          </Paper>
                        ))}
                      </Stack>
                    )}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Stack>
      </Stack>
    );
  }

  if (view === 'field-mode') {
    const loginRowsByRole = seededFieldLoginRows
      .filter((person) => fieldFolderRoleLabels.includes(person.roleLabel))
      .reduce((groups, person) => {
        const rows = groups.get(person.roleLabel) ?? [];
        rows.push(person);
        groups.set(person.roleLabel, rows);
        return groups;
      }, new Map<string, FieldPersonnel[]>());

    return (
      <Stack spacing={1.5}>
        <Typography variant="h4" fontWeight={900}>Admin</Typography>
        <Typography variant="h6" fontWeight={900}>Field Login Folders</Typography>
        {fieldFolderRoleLabels.map((role) => {
          const people = loginRowsByRole.get(role) ?? [];
          if (people.length === 0) return null;
          return (
            <Accordion key={role} disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={900}>{fieldFolderRoleTitles[role] ?? role}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={1.5}>
                  {people.map((person) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={person.id}>
                      <PersonnelFolderCard
                        name={person.name}
                        email={person.email}
                        credentialHint={person.credentialHint}
                      />
                    </Grid>
                  ))}
                </Grid>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Stack>
    );
  }

  if (view === 'history') {
    return (
      <Stack spacing={1.5}>
        <Typography variant="h4" fontWeight={900}>Admin</Typography>
        <Typography variant="h6" fontWeight={900}>App History</Typography>
        <Alert severity="info">Admin-only audit trail of app actions, approvals, returns, payments, status changes, and linked records.</Alert>
        {auditError && <Alert severity="error" onClose={() => setAuditError(null)}>{auditError}</Alert>}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField
            size="small"
            label="Search history"
            value={auditSearch}
            onChange={(event) => setAuditSearch(event.target.value)}
            sx={{ flex: 1 }}
          />
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadAudit} disabled={auditLoading}>
            Refresh
          </Button>
        </Stack>
        {auditLoading && <LinearProgress />}
        <Paper variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden', bgcolor: '#fff' }}>
          <Table size="small" sx={{ '& td, & th': { py: 0.65, px: 1, fontSize: 12 } }}>
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>Who</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Record</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {auditRows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{formatDateTime(row.occurredAt)}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={800}>{row.actorName || row.actorUserId || 'System'}</Typography>
                    <Typography variant="caption" color="text.secondary">{humanize(row.actorRole)}</Typography>
                  </TableCell>
                  <TableCell>{humanize(row.action)}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{humanize(row.entityType)}</Typography>
                    <Typography variant="caption" color="text.secondary">{row.taskId ? `Task ${row.taskId}` : row.entityId ?? '-'}</Typography>
                  </TableCell>
                  <TableCell>
                    {row.fromState || row.toState ? (
                      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                        {row.fromState && <Chip size="small" variant="outlined" label={humanize(row.fromState)} />}
                        <Typography variant="caption">to</Typography>
                        {row.toState && <Chip size="small" label={humanize(row.toState)} />}
                      </Stack>
                    ) : '-'}
                  </TableCell>
                  <TableCell>{row.notes || row.reasonCode || '-'}</TableCell>
                </TableRow>
              ))}
              {!auditRows.length && !auditLoading && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography color="text.secondary">No audit history found.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      </Stack>
    );
  }

  return (
    <Stack spacing={1.5}>
      <Typography variant="h4" fontWeight={900}>Admin</Typography>
      {statusAlerts}
      <Grid container spacing={1}>
        <Grid item xs={3}>{metric('App users', String(metrics.totalUsers), 'active logins')}</Grid>
        <Grid item xs={3}>{metric('Active teams', String(metrics.activeTeams), 'with members')}</Grid>
        <Grid item xs={3}>{metric('Unallocated users', String(metrics.unallocatedUsers), 'need team')}</Grid>
        <Grid item xs={3}>{metric('Telegram linked', String(metrics.telegramLinked), 'of all users')}</Grid>
      </Grid>
      <Box>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="caption" color="text.secondary">Team allocation</Typography>
          <Typography variant="caption" fontWeight={800}>{metrics.allocationPercent}%</Typography>
        </Stack>
        <LinearProgress variant="determinate" value={metrics.allocationPercent} sx={{ height: 8, borderRadius: 1, mt: 0.5 }} />
      </Box>
      {menuBar('Users', 'App users, logins, phones, and Telegram link status', '/field/admin/users')}
      {menuBar('Teams', 'Project employee list and team assignment', '/field/admin/teams')}
      {menuBar('Field Mode', 'Oversight of every field-mode screen available to each user role', '/field/admin/field-mode')}
      {menuBar('History', 'Admin-only app audit trail of who did what and when', '/field/admin/history')}
    </Stack>
  );
}
